import React from 'react';
import { FaCheck, FaCrown } from 'react-icons/fa';

/**
 * Subscription Plan Card Component
 * Follows Single Responsibility Principle - displays plan information
 * Follows Open/Closed Principle - extensible through props
 * 
 * @param {Object} plan - Plan data object
 * @param {boolean} isCurrentPlan - Whether this is user's current plan
 * @param {boolean} isRecommended - Whether to show "Most Popular" badge
 * @param {Function} onSelect - Callback when plan is selected
 * @param {boolean} loading - Loading state
 */
const PlanCard = ({ 
  plan, 
  isCurrentPlan = false, 
  isRecommended = false, 
  onSelect, 
  loading = false 
}) => {
  // Tier color mapping
  const getTierColor = (planName) => {
    const name = planName?.toLowerCase() || '';
    if (name.includes('premium')) return 'purple';
    if (name.includes('basic')) return 'blue';
    if (name.includes('institute')) return 'yellow';
    return 'gray';
  };

  const color = getTierColor(plan?.name);
  
  // Color classes mapping
  const colorClasses = {
    purple: {
      badge: 'bg-gradient-to-r from-purple-600 to-blue-600',
      border: 'border-purple-500',
      button: 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700',
      text: 'text-purple-600',
      icon: 'text-purple-600',
    },
    blue: {
      badge: 'bg-blue-600',
      border: 'border-blue-500',
      button: 'bg-blue-600 hover:bg-blue-700',
      text: 'text-blue-600',
      icon: 'text-blue-600',
    },
    yellow: {
      badge: 'bg-gradient-to-r from-yellow-500 to-orange-500',
      border: 'border-yellow-500',
      button: 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600',
      text: 'text-yellow-600',
      icon: 'text-yellow-600',
    },
    gray: {
      badge: 'bg-gray-600',
      border: 'border-gray-300',
      button: 'bg-gray-600 hover:bg-gray-700',
      text: 'text-gray-600',
      icon: 'text-gray-600',
    },
  };

  const colors = colorClasses[color];

  // Calculate savings for annual plans
  const monthlyCost = plan?.monthly_price || 0;
  const annualCost = plan?.annual_price || 0;
  const savings = monthlyCost * 12 - annualCost;

  return (
    <div 
      className={`relative bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border-2 ${
        isCurrentPlan ? colors.border : 'border-gray-200'
      } ${isRecommended ? 'scale-105' : ''}`}
    >
      {/* Recommended Badge */}
      {isRecommended && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className={`${colors.badge} text-white px-4 py-1 rounded-full text-sm font-semibold shadow-md flex items-center gap-1`}>
            <FaCrown className="text-xs" />
            Most Popular
          </span>
        </div>
      )}

      {/* Current Plan Badge */}
      {isCurrentPlan && (
        <div className="absolute top-4 right-4">
          <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
            Current Plan
          </span>
        </div>
      )}

      <div className="p-6 md:p-8">
        {/* Plan Name */}
        <h3 className="text-2xl font-bold text-gray-800 mb-2">
          {plan?.name || 'Free'}
        </h3>

        {/* Pricing */}
        <div className="mb-6">
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-bold text-gray-900">
              ₹{plan?.monthly_price || 0}
            </span>
            <span className="text-gray-500">/month</span>
          </div>
          
          {/* Annual Savings */}
          {savings > 0 && (
            <div className="mt-2">
              <span className="text-sm text-green-600 font-medium">
                Save ₹{savings} with annual plan
              </span>
            </div>
          )}
        </div>

        {/* Description */}
        {plan?.description && (
          <p className="text-gray-600 text-sm mb-6">
            {plan.description}
          </p>
        )}

        {/* Features List */}
        <div className="space-y-3 mb-8">
          {plan?.features && plan.features.length > 0 ? (
            plan.features.slice(0, 6).map((feature, index) => (
              <div key={index} className="flex items-start gap-2">
                <FaCheck className={`${colors.icon} mt-1 flex-shrink-0`} />
                <span className="text-gray-700 text-sm">{feature}</span>
              </div>
            ))
          ) : (
            <>
              <div className="flex items-start gap-2">
                <FaCheck className={`${colors.icon} mt-1`} />
                <span className="text-gray-700 text-sm">Limited features</span>
              </div>
            </>
          )}

          {/* Expandable features */}
          {plan?.features && plan.features.length > 6 && (
            <button className={`text-sm ${colors.text} hover:underline font-medium`}>
              + See all {plan.features.length} features
            </button>
          )}
        </div>

        {/* CTA Button */}
        <button
          onClick={() => onSelect && onSelect(plan)}
          disabled={loading || isCurrentPlan}
          className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all duration-200 transform active:scale-95 ${
            isCurrentPlan
              ? 'bg-gray-400 cursor-not-allowed'
              : colors.button
          } ${loading ? 'opacity-50 cursor-wait' : ''}`}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </span>
          ) : isCurrentPlan ? (
            'Current Plan'
          ) : (
            `Choose ${plan?.name || 'Plan'}`
          )}
        </button>
      </div>
    </div>
  );
};

export default PlanCard;
